import { supabase } from '../lib/supabase'
import { userLogsService } from './userLogs'
import { notificationsService } from './notifications'
import {
  extractMentionUserIds,
  mentionPreviewText,
} from '../components/dashboard/social/mentionUtils'
import {
  SocialComment,
  SocialEdit,
  SocialPost,
  SocialPostImage,
  SocialReaction,
  SOCIAL_POST_IMAGE_MAX_BYTES,
  SOCIAL_POST_IMAGE_MIME_TYPES,
  SOCIAL_POST_MAX_IMAGES,
} from '../types/social'

export const SOCIAL_POST_QUERY_PARAM = 'socialPost'

const SOCIAL_POSTS_BUCKET = 'social-posts'
const SIGNED_URL_EXPIRES_IN = 3600

const POST_SELECT = `
  *,
  author:author_id(id, name, avatar_url),
  reactions:social_post_reactions(id, post_id, user_id, emoji, created_at, user:user_id(id, name, avatar_url)),
  comments:social_post_comments(
    id, post_id, author_id, content, created_at, updated_at, edited_at,
    author:author_id(id, name, avatar_url)
  ),
  images:social_post_images(
    id, post_id, file_path, file_name, file_size, mime_type, uploaded_by, sort_order, created_at
  )
`

const IMAGE_SELECT =
  'id, post_id, file_path, file_name, file_size, mime_type, uploaded_by, sort_order, created_at'

const COMMENT_SELECT = `
  *,
  author:author_id(id, name, avatar_url)
`

const EDIT_SELECT = `
  *,
  editor:edited_by(id, name, avatar_url)
`

const REACTION_SELECT = '*, user:user_id(id, name, avatar_url)'

async function requireUser() {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Usuário não autenticado')
  return user
}

interface PostLogRow {
  id: string
  content: string
  author_id: string
  author?: { name?: string | null } | null
}

interface CommentLogRow {
  id: string
  post_id: string
  content: string
  author_id: string
  author?: { name?: string | null } | null
}

function postLogSnapshot(post: Pick<PostLogRow, 'id' | 'content' | 'author_id'>) {
  return {
    id: post.id,
    content: post.content,
    author_id: post.author_id,
  }
}

function commentLogSnapshot(comment: Pick<CommentLogRow, 'id' | 'post_id' | 'content' | 'author_id'>) {
  return {
    id: comment.id,
    post_id: comment.post_id,
    content: comment.content,
    author_id: comment.author_id,
  }
}

function postContentPreview(content: string, max = 80): string {
  const line = content.replace(/\s+/g, ' ').trim()
  if (!line) return '(imagem)'
  return line.length > max ? `${line.slice(0, max)}…` : line
}

function validateImageFiles(files: File[]): void {
  if (files.length > SOCIAL_POST_MAX_IMAGES) {
    throw new Error(`Máximo de ${SOCIAL_POST_MAX_IMAGES} imagens por postagem.`)
  }
  const allowed = new Set<string>(SOCIAL_POST_IMAGE_MIME_TYPES)
  for (const file of files) {
    if (!allowed.has(file.type)) {
      throw new Error(
        `Tipo de arquivo não permitido: ${file.name}. Use JPEG, PNG, GIF ou WebP.`
      )
    }
    if (file.size > SOCIAL_POST_IMAGE_MAX_BYTES) {
      const maxMb = SOCIAL_POST_IMAGE_MAX_BYTES / (1024 * 1024)
      throw new Error(`A imagem "${file.name}" excede o limite de ${maxMb} MB.`)
    }
  }
}

function fileExtension(file: File): string {
  const fromName = file.name.split('.').pop()?.toLowerCase()
  if (fromName && /^[a-z0-9]+$/.test(fromName)) return fromName
  if (file.type === 'image/jpeg') return 'jpg'
  if (file.type === 'image/png') return 'png'
  if (file.type === 'image/gif') return 'gif'
  if (file.type === 'image/webp') return 'webp'
  return 'jpg'
}

async function signImageUrls(images: SocialPostImage[]): Promise<SocialPostImage[]> {
  if (!images.length) return []
  const results = await Promise.all(
    images.map(async (img) => {
      try {
        const { data, error } = await supabase.storage
          .from(SOCIAL_POSTS_BUCKET)
          .createSignedUrl(img.file_path, SIGNED_URL_EXPIRES_IN)
        if (error || !data?.signedUrl) {
          console.warn('[socialService] signed URL failed:', img.file_path, error)
          return { ...img, url: undefined }
        }
        return { ...img, url: data.signedUrl }
      } catch (err) {
        console.warn('[socialService] signed URL error:', img.file_path, err)
        return { ...img, url: undefined }
      }
    })
  )
  return results
}

async function softDeletePost(postId: string, userId: string): Promise<void> {
  await supabase
    .from('social_posts')
    .update({
      deleted_at: new Date().toISOString(),
      deleted_by: userId,
    })
    .eq('id', postId)
    .is('deleted_at', null)
}

async function uploadPostImages(
  userId: string,
  postId: string,
  files: File[]
): Promise<SocialPostImage[]> {
  const uploadedPaths: string[] = []
  const rows: SocialPostImage[] = []

  try {
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const ext = fileExtension(file)
      const filePath = `${userId}/${postId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`

      const { error: storageError } = await supabase.storage
        .from(SOCIAL_POSTS_BUCKET)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type || 'image/jpeg',
        })

      if (storageError) throw storageError
      uploadedPaths.push(filePath)

      const { data, error: dbError } = await supabase
        .from('social_post_images')
        .insert({
          post_id: postId,
          file_path: filePath,
          file_name: file.name,
          file_size: file.size,
          mime_type: file.type || null,
          uploaded_by: userId,
          sort_order: i,
        })
        .select(IMAGE_SELECT)
        .single()

      if (dbError) throw dbError
      rows.push(data as SocialPostImage)
    }
  } catch (err) {
    if (uploadedPaths.length) {
      try {
        await supabase.storage.from(SOCIAL_POSTS_BUCKET).remove(uploadedPaths)
      } catch {
        /* ignore cleanup errors */
      }
    }
    throw err
  }

  return signImageUrls(rows)
}

async function fetchPostForLog(postId: string): Promise<PostLogRow | null> {
  const { data } = await supabase
    .from('social_posts')
    .select('id, content, author_id, author:author_id(name)')
    .eq('id', postId)
    .maybeSingle()

  return (data as PostLogRow | null) ?? null
}

async function fetchCommentForLog(commentId: string): Promise<CommentLogRow | null> {
  const { data } = await supabase
    .from('social_post_comments')
    .select('id, post_id, content, author_id, author:author_id(name)')
    .eq('id', commentId)
    .maybeSingle()

  return (data as CommentLogRow | null) ?? null
}

async function logPostCreate(post: SocialPost): Promise<void> {
  try {
    const preview = postContentPreview(post.content)
    await userLogsService.logCreate(
      'social_post',
      post.id,
      `Publicou postagem no feed: "${preview}"`,
      { new_values: postLogSnapshot(post) }
    )
  } catch (logError) {
    console.warn('Erro ao registrar log (feed — criar post):', logError)
  }
}

async function logPostUpdate(
  previous: Pick<PostLogRow, 'id' | 'content' | 'author_id'>,
  updated: SocialPost
): Promise<void> {
  try {
    const preview = postContentPreview(updated.content)
    await userLogsService.logUpdate(
      'social_post',
      updated.id,
      `Editou postagem no feed: "${preview}"`,
      postLogSnapshot(previous),
      postLogSnapshot(updated)
    )
  } catch (logError) {
    console.warn('Erro ao registrar log (feed — editar post):', logError)
  }
}

async function logPostDelete(post: PostLogRow, deletedByUserId: string): Promise<void> {
  try {
    const preview = postContentPreview(post.content)
    const isModeration = post.author_id !== deletedByUserId
    const authorName = post.author?.name?.trim() || 'Usuário'
    const description = isModeration
      ? `Excluiu postagem de ${authorName} no feed (moderação): "${preview}"`
      : `Excluiu postagem no feed: "${preview}"`

    await userLogsService.logDelete('social_post', post.id, description, {
      old_values: postLogSnapshot(post),
      additional_info: isModeration ? 'moderacao' : undefined,
    })
  } catch (logError) {
    console.warn('Erro ao registrar log (feed — excluir post):', logError)
  }
}

async function logCommentDelete(comment: CommentLogRow, deletedByUserId: string): Promise<void> {
  try {
    const preview = postContentPreview(comment.content)
    const isModeration = comment.author_id !== deletedByUserId
    const authorName = comment.author?.name?.trim() || 'Usuário'
    const description = isModeration
      ? `Excluiu comentário de ${authorName} no feed (moderação): "${preview}"`
      : `Excluiu comentário no feed: "${preview}"`

    await userLogsService.logDelete('social_post_comment', comment.id, description, {
      old_values: commentLogSnapshot(comment),
      additional_info: isModeration ? 'moderacao' : undefined,
    })
  } catch (logError) {
    console.warn('Erro ao registrar log (feed — excluir comentário):', logError)
  }
}

function buildSocialPostUrl(postId: string): string | null {
  if (typeof window === 'undefined') return null
  return `${window.location.origin}/dashboard?${SOCIAL_POST_QUERY_PARAM}=${postId}`
}

function appendPostLink(body: string, postId: string): string {
  const url = buildSocialPostUrl(postId)
  if (!url) return body
  return `${body.trimEnd()}\n\n🔗 ${url}`
}

async function notifyMentionedInNewPost(post: SocialPost): Promise<void> {
  const authorId = post.author_id
  const authorName = post.author?.name?.trim() || 'Usuário'
  const userIds = extractMentionUserIds(post.content).filter((id) => id !== authorId)
  if (userIds.length === 0) return

  const title = `${authorName} marcou você no feed`
  const body = appendPostLink(mentionPreviewText(post.content), post.id)

  const { error } = await notificationsService.sendToGroups({
    title,
    body,
    userIds,
    createdByUserId: authorId,
  })

  if (error) {
    console.warn('[socialService.createPost] notifyMentionedInNewPost:', error)
  }
}

async function notifyMentionedInNewComment(comment: SocialComment): Promise<void> {
  const authorId = comment.author_id
  const authorName = comment.author?.name?.trim() || 'Usuário'
  const userIds = extractMentionUserIds(comment.content).filter((id) => id !== authorId)
  if (userIds.length === 0) return

  const title = `${authorName} marcou você em um comentário`
  const body = appendPostLink(mentionPreviewText(comment.content), comment.post_id)

  const { error } = await notificationsService.sendToGroups({
    title,
    body,
    userIds,
    createdByUserId: authorId,
  })

  if (error) {
    console.warn('[socialService.addComment] notifyMentionedInNewComment:', error)
  }
}

async function normalizePostRow(row: any): Promise<SocialPost> {
  const imagesRaw = (row.images || []) as SocialPostImage[]
  const imagesSorted = [...imagesRaw].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
  const images = await signImageUrls(imagesSorted)

  return {
    ...row,
    comments: (row.comments || []).sort(
      (a: SocialComment, b: SocialComment) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    ),
    comment_count: (row.comments || []).length,
    images,
  } as SocialPost
}

export const socialService = {
  async listPosts(limit = 30): Promise<SocialPost[]> {
    const { data, error } = await supabase
      .from('social_posts')
      .select(POST_SELECT)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error

    return Promise.all((data || []).map((row) => normalizePostRow(row)))
  },

  async getPostById(postId: string): Promise<SocialPost | null> {
    const { data, error } = await supabase
      .from('social_posts')
      .select(POST_SELECT)
      .eq('id', postId)
      .is('deleted_at', null)
      .maybeSingle()

    if (error) throw error
    if (!data) return null
    return normalizePostRow(data)
  },

  async createPost(content: string, files?: File[]): Promise<SocialPost> {
    const user = await requireUser()
    const trimmed = content.trim()
    const imageFiles = files?.length ? files.slice(0, SOCIAL_POST_MAX_IMAGES) : []

    if (!trimmed && imageFiles.length === 0) {
      throw new Error('Escreva algo ou anexe ao menos uma imagem.')
    }
    if (imageFiles.length > 0) {
      validateImageFiles(imageFiles)
    }

    const { data, error } = await supabase
      .from('social_posts')
      .insert({ author_id: user.id, content: trimmed || '' })
      .select(
        `
        *,
        author:author_id(id, name, avatar_url)
      `
      )
      .single()

    if (error) throw error

    let images: SocialPostImage[] = []
    try {
      if (imageFiles.length > 0) {
        images = await uploadPostImages(user.id, data.id, imageFiles)
      }
    } catch (uploadErr) {
      await softDeletePost(data.id, user.id)
      const msg =
        uploadErr instanceof Error
          ? uploadErr.message
          : 'Erro ao enviar imagens da postagem'
      throw new Error(
        `${msg}. Se o erro persistir, execute frontend/sqls/social_05_post_images.sql no Supabase.`
      )
    }

    const post: SocialPost = {
      ...data,
      comments: [],
      comment_count: 0,
      reactions: [],
      images,
    }
    await logPostCreate(post)
    void notifyMentionedInNewPost(post).catch((err) =>
      console.warn('[socialService.createPost] notifyMentionedInNewPost:', err)
    )
    return post
  },

  async updatePost(postId: string, content: string): Promise<SocialPost> {
    await requireUser()
    const trimmed = content.trim()

    const previous = await fetchPostForLog(postId)
    if (!trimmed) {
      const current = await this.getPostById(postId)
      if (!current?.images?.length) {
        throw new Error('Conteúdo não pode estar vazio')
      }
    }

    const { data, error } = await supabase
      .from('social_posts')
      .update({ content: trimmed || '' })
      .eq('id', postId)
      .is('deleted_at', null)
      .select(POST_SELECT)
      .single()

    if (error) throw error
    const post = await normalizePostRow(data)
    if (previous && previous.content !== (trimmed || '')) {
      await logPostUpdate(previous, post)
    }
    return post
  },

  async deletePost(postId: string): Promise<void> {
    const user = await requireUser()
    const post = await fetchPostForLog(postId)

    const { data, error } = await supabase
      .from('social_posts')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: user.id,
      })
      .eq('id', postId)
      .is('deleted_at', null)
      .select('id')
      .maybeSingle()

    if (error) {
      const hint =
        error.message?.includes('deleted_at') || error.message?.includes('deleted_by')
          ? ' Execute o SQL frontend/sqls/social_04_fix_soft_delete_rls.sql no Supabase.'
          : ''
      throw new Error(`${error.message || 'Erro ao excluir post'}${hint}`)
    }

    if (!data) {
      throw new Error(
        'Não foi possível excluir a postagem (sem permissão ou script de exclusão lógica não aplicado). Execute frontend/sqls/social_04_fix_soft_delete_rls.sql no Supabase.'
      )
    }

    if (post) await logPostDelete(post, user.id)
  },

  async listComments(postId: string): Promise<SocialComment[]> {
    const { data, error } = await supabase
      .from('social_post_comments')
      .select(COMMENT_SELECT)
      .eq('post_id', postId)
      .order('created_at', { ascending: true })

    if (error) throw error
    return (data || []) as SocialComment[]
  },

  async addComment(postId: string, content: string): Promise<SocialComment> {
    const user = await requireUser()
    const trimmed = content.trim()
    if (!trimmed) throw new Error('Conteúdo não pode estar vazio')

    const { data, error } = await supabase
      .from('social_post_comments')
      .insert({ post_id: postId, author_id: user.id, content: trimmed })
      .select(COMMENT_SELECT)
      .single()

    if (error) throw error
    const comment = data as SocialComment
    void notifyMentionedInNewComment(comment).catch((err) =>
      console.warn('[socialService.addComment] notifyMentionedInNewComment:', err)
    )
    return comment
  },

  async updateComment(commentId: string, content: string): Promise<SocialComment> {
    await requireUser()
    const trimmed = content.trim()
    if (!trimmed) throw new Error('Conteúdo não pode estar vazio')

    const { data, error } = await supabase
      .from('social_post_comments')
      .update({ content: trimmed })
      .eq('id', commentId)
      .select(COMMENT_SELECT)
      .single()

    if (error) throw error
    return data as SocialComment
  },

  async deleteComment(commentId: string): Promise<void> {
    const user = await requireUser()
    const comment = await fetchCommentForLog(commentId)

    const { error } = await supabase.from('social_post_comments').delete().eq('id', commentId)
    if (error) throw error

    if (comment) await logCommentDelete(comment, user.id)
  },

  async setReaction(postId: string, emoji: string): Promise<SocialReaction | null> {
    const user = await requireUser()

    const { data: existing } = await supabase
      .from('social_post_reactions')
      .select('id, emoji')
      .eq('post_id', postId)
      .eq('user_id', user.id)
      .maybeSingle()

    if (existing) {
      if (existing.emoji === emoji) {
        const { error } = await supabase
          .from('social_post_reactions')
          .delete()
          .eq('id', existing.id)
        if (error) throw error
        return null
      }

      const { data, error } = await supabase
        .from('social_post_reactions')
        .update({ emoji })
        .eq('id', existing.id)
        .select(REACTION_SELECT)
        .single()

      if (error) throw error
      return data as SocialReaction
    }

    const { data, error } = await supabase
      .from('social_post_reactions')
      .insert({ post_id: postId, user_id: user.id, emoji })
      .select(REACTION_SELECT)
      .single()

    if (error) throw error
    return data as SocialReaction
  },

  async searchMentionUsers(query: string, limit = 8): Promise<Array<{ id: string; name?: string | null; avatar_url?: string | null }>> {
    const q = query.trim().replace(/[%_\\]/g, '\\$&')
    const builder = supabase
      .from('profiles')
      .select('id, name, avatar_url')
      .eq('is_active', true)
      .order('name')
      .limit(limit)

    const { data, error } = q
      ? await builder.ilike('name', `%${q}%`)
      : await builder

    if (error) throw error
    return data || []
  },

  async listEditHistory(params: {
    postId?: string
    commentId?: string
  }): Promise<SocialEdit[]> {
    let query = supabase
      .from('social_edit_history')
      .select(EDIT_SELECT)
      .order('edited_at', { ascending: false })

    if (params.postId) {
      query = query.eq('post_id', params.postId)
    } else if (params.commentId) {
      query = query.eq('comment_id', params.commentId)
    } else {
      throw new Error('Informe postId ou commentId')
    }

    const { data, error } = await query
    if (error) throw error
    return (data || []) as SocialEdit[]
  },
}
